/**
 * Raw shape of an item as it may be read out of the DynamoDB logs table.
 * Every attribute is `unknown` because DynamoDB items are schemaless and a
 * production table may contain historical rows written by older code paths
 * (string dates vs. epoch numbers, JSON-string vs. map `input`/`response`,
 * derived attributes present or absent, etc). Nothing here may be trusted
 * without going through the normalizer.
 */
export interface DynamoDbLogItem {
  [key: string]: unknown;

  userId?: unknown;
  createdAt?: unknown;
  id?: unknown;
  input?: unknown;
  method?: unknown;
  response?: unknown;
  responseStored?: unknown;
  route?: unknown;
  updatedAt?: unknown;

  statusCode?: unknown;
  errorClassification?: unknown;
  errorType?: unknown;
  errorMessage?: unknown;
  normalizedRoute?: unknown;
  createdAtEpoch?: unknown;
  updatedAtEpoch?: unknown;
  eventDate?: unknown;
  eventHour?: unknown;

  userIndexKey?: unknown;
  routeIndexKey?: unknown;
  errorIndexKey?: unknown;
  methodIndexKey?: unknown;
  statusIndexKey?: unknown;
  dateIndexKey?: unknown;

  isFixed?: unknown;
  fixedIndexKey?: unknown;
}

export type ErrorClassification = "error" | "success" | "unknown" | "malformed";

export const ERROR_CLASSIFICATIONS: readonly ErrorClassification[] = [
  "error",
  "success",
  "unknown",
  "malformed",
];

export interface NormalizedLogRecord {
  userId: string;
  createdAt: Date | null;
  id: string;

  inputRaw: string;
  inputParsed: unknown;
  inputMalformed: boolean;

  method: string;

  responseRaw: string;
  responseParsed: unknown;
  responseMalformed: boolean;

  responseStored: boolean;

  route: string;
  normalizedRoute: string;

  updatedAt: Date | null;

  errorClassification: ErrorClassification;
  isError: boolean | null;

  statusCode?: number;
  errorType?: string;
  errorMessage?: string;

  isFixed: boolean;
}

export interface SerializedLogRecord
  extends Omit<NormalizedLogRecord, "createdAt" | "updatedAt"> {
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DerivedErrorDetails {
  isError: boolean | null;
  classification: ErrorClassification;
  statusCode?: number;
  errorType?: string;
  errorMessage?: string;
}
